import {
  ArrowUpRight,
  CircleCheck,
  FileSearch,
  Inbox,
  Network,
  Pause,
  Play,
  Radar,
  Route,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import {
  m,
  useAnimationFrame,
  useInView,
  useMotionValue,
  useReducedMotion,
} from 'motion/react'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

const expressiveEase = [0, 0, 0.3, 1] as const

type RectBounds = Pick<DOMRect, 'bottom' | 'left' | 'right' | 'top'>

export function pointerDistanceToRect(
  clientX: number,
  clientY: number,
  rect: RectBounds,
) {
  const dx = Math.max(rect.left - clientX, 0, clientX - rect.right)
  const dy = Math.max(rect.top - clientY, 0, clientY - rect.bottom)
  return Math.hypot(dx, dy)
}

export function signalOpacityForDistance(
  distance: number,
  baseOpacity = 0.42,
  disappearDistance = 78,
  restoreDistance = 250,
) {
  if (distance <= disappearDistance) return 0
  if (distance >= restoreDistance) return baseOpacity
  const progress = (distance - disappearDistance) / (restoreDistance - disappearDistance)
  const smoothProgress = progress * progress * (3 - 2 * progress)
  return baseOpacity * smoothProgress
}

export function PremiumReveal({
  children,
  className = '',
  delay = 0,
}: {
  children: ReactNode
  className?: string
  delay?: number
}) {
  const reducedMotion = useReducedMotion()

  return (
    <m.div
      className={className}
      initial={reducedMotion ? false : { opacity: 0, y: 24, scale: 0.988 }}
      transition={{ delay, duration: 0.7, ease: expressiveEase }}
      viewport={{ amount: 0.18, once: true }}
      whileInView={reducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
    >
      {children}
    </m.div>
  )
}

const heroSignals = [
  {
    className: 'is-signal',
    detail: 'New line planned for Q4',
    icon: Radar,
    label: 'Plant expansion detected',
  },
  {
    className: 'is-proof',
    detail: 'Known / found / interpreted',
    icon: FileSearch,
    label: 'Evidence chain attached',
  },
  {
    className: 'is-route',
    detail: 'Owner and next step ready',
    icon: Route,
    label: 'Qualified handoff',
  },
] as const

export function HeroStageSignals() {
  const reducedMotion = useReducedMotion()
  const rootRef = useRef<HTMLDivElement>(null)
  const signalRefs = useRef<Array<HTMLDivElement | null>>([])

  useEffect(() => {
    const root = rootRef.current
    const stage = root?.parentElement
    if (!root || !stage) return

    let pointerFrame: number | null = null
    let touchRestoreTimer: number | null = null
    let latestPointer: { clientX: number; clientY: number } | null = null
    let focusWithin = false

    const setAllOpacity = (opacity: number) => {
      for (const signal of signalRefs.current) {
        signal?.style.setProperty('--signal-proximity-opacity', opacity.toFixed(3))
      }
    }

    const applyPointerProximity = () => {
      pointerFrame = null
      if (!latestPointer || focusWithin) return
      root.dataset.interacting = 'true'

      for (const signal of signalRefs.current) {
        if (!signal) continue
        const distance = pointerDistanceToRect(
          latestPointer.clientX,
          latestPointer.clientY,
          signal.getBoundingClientRect(),
        )
        signal.style.setProperty(
          '--signal-proximity-opacity',
          signalOpacityForDistance(distance).toFixed(3),
        )
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || focusWithin) return
      latestPointer = { clientX: event.clientX, clientY: event.clientY }
      if (pointerFrame !== null) return
      pointerFrame = window.requestAnimationFrame(applyPointerProximity)
    }

    const handlePointerLeave = () => {
      latestPointer = null
      delete root.dataset.interacting
      if (!focusWithin) setAllOpacity(0.62)
    }

    const handlePointerDown = () => {
      root.dataset.interacting = 'true'
      setAllOpacity(0)
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (event.pointerType !== 'touch' || focusWithin) return
      if (touchRestoreTimer !== null) window.clearTimeout(touchRestoreTimer)
      touchRestoreTimer = window.setTimeout(() => {
        delete root.dataset.interacting
        setAllOpacity(0.62)
      }, 700)
    }

    const handleFocusIn = () => {
      focusWithin = true
      root.dataset.interacting = 'true'
      setAllOpacity(0)
    }

    const handleFocusOut = (event: FocusEvent) => {
      const nextTarget = event.relatedTarget
      if (nextTarget instanceof Node && stage.contains(nextTarget)) return
      focusWithin = false
      if (latestPointer) {
        applyPointerProximity()
      } else {
        delete root.dataset.interacting
        setAllOpacity(0.62)
      }
    }

    setAllOpacity(0.62)
    stage.addEventListener('pointermove', handlePointerMove)
    stage.addEventListener('pointerleave', handlePointerLeave)
    stage.addEventListener('pointerdown', handlePointerDown)
    stage.addEventListener('pointerup', handlePointerUp)
    stage.addEventListener('focusin', handleFocusIn)
    stage.addEventListener('focusout', handleFocusOut)

    return () => {
      if (pointerFrame !== null) window.cancelAnimationFrame(pointerFrame)
      if (touchRestoreTimer !== null) window.clearTimeout(touchRestoreTimer)
      stage.removeEventListener('pointermove', handlePointerMove)
      stage.removeEventListener('pointerleave', handlePointerLeave)
      stage.removeEventListener('pointerdown', handlePointerDown)
      stage.removeEventListener('pointerup', handlePointerUp)
      stage.removeEventListener('focusin', handleFocusIn)
      stage.removeEventListener('focusout', handleFocusOut)
    }
  }, [reducedMotion])

  return (
    <div aria-hidden="true" className="hero-stage-signals" ref={rootRef}>
      {heroSignals.map((signal, index) => {
        const Icon = signal.icon
        return (
          <div
            className={`hero-stage-signal-anchor ${signal.className}`}
            key={signal.label}
            ref={(node) => {
              signalRefs.current[index] = node
            }}
          >
            <m.div
              animate={reducedMotion ? undefined : { opacity: 1, scale: 1, y: 0 }}
              className="hero-stage-signal"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.92, y: 18 }}
              transition={{
                delay: 0.72 + index * 0.13,
                duration: 0.64,
                ease: expressiveEase,
              }}
            >
              <span><Icon /></span>
              <span><strong>{signal.label}</strong><small>{signal.detail}</small></span>
              <ArrowUpRight />
            </m.div>
          </div>
        )
      })}
    </div>
  )
}

function useTrackMeasurement(
  trackRef: React.RefObject<HTMLDivElement | null>,
  axis: 'x' | 'y',
  direction: 1 | -1,
  position: ReturnType<typeof useMotionValue<number>>,
) {
  const segment = useRef(1)

  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return

    const measure = () => {
      const nextSegment = axis === 'x' ? track.scrollWidth / 2 : track.scrollHeight / 2
      if (!Number.isFinite(nextSegment) || nextSegment <= 0) return
      segment.current = nextSegment
      position.set(direction === 1 ? -nextSegment : 0)
    }

    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(track)
    window.addEventListener('resize', measure)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [axis, direction, position, trackRef])

  return segment
}

function KineticSignalTrack({
  direction,
  items,
  paused,
  row,
}: {
  direction: 1 | -1
  items: readonly string[]
  paused: boolean
  row: number
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const position = useMotionValue(0)
  const segment = useTrackMeasurement(trackRef, 'x', direction, position)
  const reducedMotion = useReducedMotion()
  const inView = useInView(trackRef, { margin: '180px 0px' })
  const [hovered, setHovered] = useState(false)
  const currentSpeed = useRef(0)

  useAnimationFrame((_time, delta) => {
    if (reducedMotion || !inView || segment.current <= 1) return
    const targetSpeed = paused ? 0 : hovered ? 7 : 34 + row * 4
    const smoothing = Math.min(1, delta / (hovered || paused ? 420 : 760))
    currentSpeed.current += (targetSpeed - currentSpeed.current) * smoothing
    let next = position.get() + direction * currentSpeed.current * (delta / 1000)

    if (direction === -1 && next <= -segment.current) next += segment.current
    if (direction === 1 && next >= 0) next -= segment.current
    position.set(next)
  })

  const rotated = [...items.slice(row), ...items.slice(0, row)]
  const copies = [...rotated, ...rotated]

  return (
    <div
      className="kinetic-signal-viewport"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <m.div
        aria-hidden="true"
        className="kinetic-signal-track"
        ref={trackRef}
        style={{ x: reducedMotion ? 0 : position }}
      >
        {copies.map((item, index) => (
          <span className={`signal-tone-${(index + row) % 5}`} key={`${row}-${item}-${index}`}>
            <i />
            {item}
          </span>
        ))}
      </m.div>
    </div>
  )
}

export function KineticSignalField({ items }: { items: readonly string[] }) {
  const [paused, setPaused] = useState(false)
  const reducedMotion = useReducedMotion()

  return (
    <div className="kinetic-signal-field">
      <div className="kinetic-signal-toolbar">
        <span><Sparkles /> Representative market activity</span>
        {reducedMotion ? (
          <span className="motion-preference-status"><Pause /> Motion reduced</span>
        ) : (
          <button
            aria-label={paused ? 'Play moving market signals' : 'Pause moving market signals'}
            aria-pressed={paused}
            onClick={() => setPaused((current) => !current)}
            type="button"
          >
            {paused ? <Play /> : <Pause />}
            {paused ? 'Play motion' : 'Pause motion'}
          </button>
        )}
      </div>
      <p className="sr-only">{items.join('. ')}</p>
      <div className="kinetic-signal-lanes">
        <KineticSignalTrack direction={-1} items={items} paused={paused} row={0} />
        <KineticSignalTrack direction={1} items={items} paused={paused} row={2} />
        <KineticSignalTrack direction={-1} items={items} paused={paused} row={4} />
      </div>
    </div>
  )
}

type Standard = {
  body?: string
  footer?: string
  label?: string
  title: string
}

type ProofWallMode = 'operating' | 'testimonial'

function KineticProofColumn({
  direction,
  items,
  paused,
  column,
  mode,
}: {
  direction: 1 | -1
  items: readonly Standard[]
  paused: boolean
  column: number
  mode: ProofWallMode
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const position = useMotionValue(0)
  const segment = useTrackMeasurement(trackRef, 'y', direction, position)
  const reducedMotion = useReducedMotion()
  const inView = useInView(trackRef, { margin: '220px 0px' })
  const [hovered, setHovered] = useState(false)
  const currentSpeed = useRef(0)

  useAnimationFrame((_time, delta) => {
    const compact = typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
    if (reducedMotion || compact || !inView || segment.current <= 1) return
    const targetSpeed = paused ? 0 : hovered ? 6 : 23 + column * 2
    const smoothing = Math.min(1, delta / (hovered || paused ? 440 : 800))
    currentSpeed.current += (targetSpeed - currentSpeed.current) * smoothing
    let next = position.get() + direction * currentSpeed.current * (delta / 1000)

    if (direction === -1 && next <= -segment.current) next += segment.current
    if (direction === 1 && next >= 0) next -= segment.current
    position.set(next)
  })

  const rotated = [...items.slice(column), ...items.slice(0, column)]
  const copies = [...rotated, ...rotated]
  const icons = [ShieldCheck, Network, Inbox, CircleCheck]

  return (
    <div
      className="proof-motion-column"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <m.div
        className="proof-motion-track"
        ref={trackRef}
        style={{ y: reducedMotion ? 0 : position }}
      >
        {copies.map((item, index) => {
          const Icon = icons[(index + column) % icons.length]
          return (
            <article key={`${column}-${item.title}-${index}`}>
              <span>
                <Icon />
                {item.label ?? `OPERATING STANDARD ${String((index % items.length) + 1).padStart(2, '0')}`}
              </span>
              <h3>{item.title}</h3>
              {item.body ? <p>{item.body}</p> : null}
              <small>
                <CircleCheck />
                {item.footer ?? (mode === 'testimonial' ? 'Publication approval required' : 'Visible in the operating record')}
              </small>
            </article>
          )
        })}
      </m.div>
    </div>
  )
}

export function OperatingProofWall({
  items,
  mode = 'operating',
}: {
  items: readonly Standard[]
  mode?: ProofWallMode
}) {
  const [paused, setPaused] = useState(false)
  const reducedMotion = useReducedMotion()
  const motionLabel = mode === 'testimonial' ? 'customer testimonial wall' : 'operating standards wall'

  useEffect(() => {
    const pauseOnHidden = () => {
      if (document.hidden) setPaused(true)
    }
    document.addEventListener('visibilitychange', pauseOnHidden)
    return () => document.removeEventListener('visibilitychange', pauseOnHidden)
  }, [])

  return (
    <div className={`proof-motion-shell proof-motion-shell-${mode}`} data-proof-wall-mode={mode}>
      <div className="proof-motion-toolbar">
        <span>
          <i />
          {mode === 'testimonial'
            ? 'Customer language · publication approval required'
            : 'Method, not invented testimonials'}
        </span>
        {reducedMotion ? (
          <span className="motion-preference-status"><Pause /> Motion reduced</span>
        ) : (
          <button
            aria-label={`${paused ? 'Play' : 'Pause'} ${motionLabel}`}
            aria-pressed={paused}
            onClick={() => setPaused((current) => !current)}
            type="button"
          >
            {paused ? <Play /> : <Pause />}
            {paused ? 'Play motion' : 'Pause motion'}
          </button>
        )}
      </div>
      <div className="proof-motion-grid">
        <KineticProofColumn column={0} direction={-1} items={items} mode={mode} paused={paused} />
        <KineticProofColumn column={1} direction={1} items={items} mode={mode} paused={paused} />
        <KineticProofColumn column={2} direction={-1} items={items} mode={mode} paused={paused} />
      </div>
    </div>
  )
}
